import { get, isEmpty } from 'lodash';
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';

import { BREADCRUMB_NAVIGATION_LINK_TYPES } from 'constants/breadcrumbNavigation';
import CustomCollectionBannerContainer from 'containers/CustomCollectionBannerContainer';
import CustomCollectionCardsListContainer from 'containers/CustomCollectionCardsListContainer';
import CustomCollectionDescriptionModuleContainer from 'containers/CustomCollectionDescriptionModuleContainer';
import CustomCollectionFaqContainer from 'containers/CustomCollectionFaqContainer';
import CustomCollectionSecondaryNavigationContainer from 'containers/CustomCollectionSecondaryNavigationContainer';
import * as actions from 'state/actions/collection';
import BreadcrumbsUtil from 'utils/Breadcrumbs';
import Log from 'utils/Log';
import Settings from 'utils/Settings';

const CustomCollectionController = class {
  static get customCollectionData() {
    const data = get(window, ['ALLBIRDS', 'customCollection']);

    if (!data) {
      Log.error(
        new Error(
          'Controllers:CustomCollectionController.customCollectionData -- missing custom collection data!'
        )
      );
    }

    return data;
  }

  static get handle() {
    return get(CustomCollectionController.customCollectionData, 'handle');
  }

  static get faqItems() {
    return get(CustomCollectionController.customCollectionData, 'faqItems');
  }

  static get descriptionModule() {
    return get(CustomCollectionController.customCollectionData, 'localizedDescriptionModule');
  }

  static setCustomCollectionData(store) {
    store.dispatch(
      actions.setCustomCollectionData(CustomCollectionController.customCollectionData)
    );
  }

  static fetchCustomCollectionData(store) {
    store.dispatch(actions.fetchCustomCollection(CustomCollectionController.handle));
  }

  static get secondaryNavigationOptions() {
    const type = BREADCRUMB_NAVIGATION_LINK_TYPES.COLLECTION;
    const { handle } = CustomCollectionController;

    const { secondaryNavigationOptions } = BreadcrumbsUtil.getSecondaryNavigationOptions({
      type,
      handle,
    });

    return secondaryNavigationOptions;
  }

  static mountCustomCollectionBannerContainer = store => {
    ReactDOM.render(
      <Provider store={store}>
        <CustomCollectionBannerContainer />
      </Provider>,
      document.getElementById('custom-collection-banner-container')
    );
  };

  static mountCustomCollectionCardsListContainer = store => {
    ReactDOM.render(
      <Provider store={store}>
        <CustomCollectionCardsListContainer />
      </Provider>,
      document.getElementById('custom-collection-cardslist-container')
    );
  };

  static mountCustomCollectionSecondaryNavigationContainer = store => {
    ReactDOM.render(
      <Provider store={store}>
        <CustomCollectionSecondaryNavigationContainer
          handle={CustomCollectionController.handle}
          secondaryNavigationOptions={CustomCollectionController.secondaryNavigationOptions}
        />
      </Provider>,
      document.getElementById('custom-collection-secondary-navigation-container')
    );
  };

  static mountCustomCollectionDescriptionModuleContainer = store => {
    ReactDOM.render(
      <Provider store={store}>
        <CustomCollectionDescriptionModuleContainer />
      </Provider>,
      document.getElementById('custom-collection-description-module-container')
    );
  };

  static mountCustomCollectionFaqContainer = store => {
    ReactDOM.render(
      <Provider store={store}>
        <CustomCollectionFaqContainer />
      </Provider>,
      document.getElementById('custom-collection-faq-container')
    );
  };

  static addStructuredData = store => {
    store.dispatch(
      actions.setCustomCollectionPageStructuredData({
        faqItems: CustomCollectionController.faqItems,
        description: get(CustomCollectionController.descriptionModule, [Settings.currentLocale]),
        name: get(CustomCollectionController.customCollectionData, 'title'),
      })
    );
  };

  static init = store => {
    CustomCollectionController.setCustomCollectionData(store);
    CustomCollectionController.fetchCustomCollectionData(store);
    CustomCollectionController.mountCustomCollectionBannerContainer(store);
    CustomCollectionController.mountCustomCollectionCardsListContainer(store);

    if (!isEmpty(CustomCollectionController.descriptionModule)) {
      CustomCollectionController.mountCustomCollectionDescriptionModuleContainer(store);
    }

    if (!isEmpty(CustomCollectionController.faqItems)) {
      CustomCollectionController.mountCustomCollectionFaqContainer(store);
      CustomCollectionController.addStructuredData(store);
    }

    if (!isEmpty(CustomCollectionController.secondaryNavigationOptions)) {
      CustomCollectionController.mountCustomCollectionSecondaryNavigationContainer(store);
    }
  };
};

export default CustomCollectionController;
